import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  ManagerProgressionTaskSlug,
  ManagerProgressionTaskState,
} from '@sorare/core/src/__generated__/globalTypes';
import { BodyM, BrandM } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DailyActionSubTask } from 'components/missions/DailyActionTask/DailyActionSubTask';
import type { DailyActionSubTask_taskInterface } from 'components/missions/DailyActionTask/DailyActionSubTask/__generated__/index.graphql';

import {
  TutorialStepAsset,
  TutorialStepContent,
  TutorialStepTextContent,
} from '../ui';

const Content = styled(TutorialStepContent)`
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: linear-gradient(to bottom, transparent 0%, var(--c-black) 100%);
  }
`;

const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  inset: 0;
`;

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

const SubTasks = styled(TutorialStepAsset)`
  pointer-events: none;
  @media ${tabletAndAbove} {
    transform: translateY(20%);
  }
`;

const messages = defineMessages({
  buyAPack: {
    id: 'DailyMissionsStep.buyAPack',
    defaultMessage: 'Buy a Pack',
  },
  levelUpACard: {
    id: 'DailyMissionsStep.levelUpACard',
    defaultMessage: 'Level Up a Card',
  },
  completeTheScrapChallenge: {
    id: 'DailyMissionsStep.completeTheScrapChallenge',
    defaultMessage: 'Complete the Scrap Challenge',
  },
  playTheDecisivePicker: {
    id: 'DailyMissionsStep.playTheDecisivePicker',
    defaultMessage: 'Play the Decisive Picker',
  },
});

export const DailyMissionsStep = () => {
  const { formatMessage } = useIntl();

  const mockTasksData: DailyActionSubTask_taskInterface[] = [
    {
      id: '1',
      title: '',
      description: formatMessage(messages.buyAPack),
      aasmState: ManagerProgressionTaskState.COMPLETED,
      name: ManagerProgressionTaskSlug.COMPLETE_TASK,
      target: 1,
      __typename: 'Task',
      taskConfigSlug: 'daily_boost',
    },
    {
      id: '2',
      title: '',
      description: formatMessage(messages.levelUpACard),
      aasmState: ManagerProgressionTaskState.READY,
      name: ManagerProgressionTaskSlug.LEVEL_UP_CARD,
      target: 1,
      __typename: 'Task',
      taskConfigSlug: 'daily_boost',
    },
    {
      id: '3',
      title: '',
      description: formatMessage(messages.completeTheScrapChallenge),
      aasmState: ManagerProgressionTaskState.READY,
      name: ManagerProgressionTaskSlug.COMPLETE_TASK,
      target: 1,
      __typename: 'Task',
      taskConfigSlug: 'daily_boost',
    },
    {
      id: '4',
      title: '',
      description: formatMessage(messages.playTheDecisivePicker),
      aasmState: ManagerProgressionTaskState.READY,
      name: ManagerProgressionTaskSlug.UPSERT_TASK_APPEARANCES,
      target: 1,
      __typename: 'Task',
      taskConfigSlug: 'daily_boost',
    },
  ];
  return (
    <Bezel borderColor="var(--c-nd-150)">
      <StyledTextureBackground
        background="missions"
        mode="dark"
        color={['transparent', 'var(--c-blue-400)']}
        imageOpacity={0.3}
        withDots
      />
      <Content>
        <SubTasks gap={0.5}>
          {mockTasksData.map(task => (
            <DailyActionSubTask
              key={task.id}
              task={task}
              onDropChallengeClick={() => {}}
              onDecisivePlayerPickerClick={() => {}}
            />
          ))}
        </SubTasks>
        <TutorialStepTextContent>
          <BrandM>
            <FormattedMessage
              id="DailyMissionsStep.title"
              defaultMessage="Daily Missions"
            />
          </BrandM>
          <BodyM color="var(--c-nd-600)">
            <FormattedMessage
              id="DailyMissionsStep.subtitle"
              defaultMessage="Log in, play, and earn your daily tickets"
            />
          </BodyM>
        </TutorialStepTextContent>
      </Content>
      <StyledNeonEdge
        color="var(--c-limited-primary)"
        showNeon="always"
        showHalo="always"
      />
    </Bezel>
  );
};
